
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import TimelineSection from "@/components/TimelineSection";
import { DEFAULT_TIMELINE_SECTIONS, MOCK_ENTRIES } from "@/data/mockData";
import { TimelineSection as TimelineSectionType } from "@/types";

const TimelineDemo = () => {
  const { t, i18n } = useTranslation('landing');
  
  // Create demo sections with proper translations
  const getDemoSections = (): TimelineSectionType[] => {
    const isEnglish = i18n.language === 'en';
    
    // Use first 3 sections for demo (Kindheit expanded, Jugend and Frühes Erwachsenenalter collapsed)
    const sectionsToShow = DEFAULT_TIMELINE_SECTIONS.slice(1, 4); // Skip Embryonale Phase, take next 3
    
    return sectionsToShow.map((section, index) => ({
      ...section,
      // Apply language selection
      name: isEnglish ? section.name_en : section.name,
      description: isEnglish ? (section.description_en || section.description) : section.description,
      cosmic_aspect: isEnglish ? (section.cosmic_aspect_en || section.cosmic_aspect) : section.cosmic_aspect,
      // Set expansion state - first section (Kindheit) expanded, others collapsed
      isExpanded: index === 0
    }));
  };

  const [demoSections] = useState<TimelineSectionType[]>(getDemoSections());

  // Get entries for each section
  const getEntriesForSection = (sectionId: string) => {
    return MOCK_ENTRIES.filter(entry => entry.profile_timeline_section_id === sectionId);
  };

  // Dummy toggle function (non-functional for demo)
  const handleToggleExpand = (sectionId: string) => {
    // Disabled for demo - no interaction
  };

  // Dummy create memory function (non-functional for demo)
  const handleCreateMemory = (sectionId: string) => {
    // Disabled for demo - no interaction
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight"
            dangerouslySetInnerHTML={{ __html: t('timelineDemo.title') }}
          />
          <p className="text-xl text-gray-600 font-normal max-w-2xl mx-auto">
            {t('timelineDemo.subtitle')}
          </p>
        </div>

        {/* Timeline Demo Container */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 relative">
            {/* Demo User Header */}
            <div className="text-center py-8 px-8 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-uplivion-blue mb-1">{t('timelineDemo.userInfo.timelineOf')}</h3>
              <p className="text-lg text-gray-600">{t('timelineDemo.userInfo.from')}</p>
              <h4 className="text-xl font-bold text-uplivion-blue mb-1">Remi Riley</h4>
              <p className="text-base">26.01.1995</p>
              <p className="text-gray-500 text-sm">{t('timelineDemo.userInfo.zodiac')}</p>
            </div>

            {/* Timeline Sections */}
            <div className="relative timeline-demo">
              {demoSections.map((section, index) => (
                <TimelineSection
                  key={section.id}
                  section={section}
                  entries={getEntriesForSection(section.id)}
                  onToggleExpand={handleToggleExpand}
                  onCreateMemory={handleCreateMemory}
                  index={index}
                />
              ))}
            </div>

            {/* Demo Overlay for non-interactive hint */}
            <div className="absolute top-4 right-4 bg-uplivion-blue/10 text-uplivion-blue px-3 py-1 rounded-full text-xs font-medium">
              {t('timelineDemo.demoLabel')}
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              {t('timelineDemo.cta.text')}
            </p>
            <Button 
              asChild 
              className="bg-uplivion-blue hover:bg-blue-700 text-white text-lg px-10 py-4 h-auto rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              <a href="https://app.uplivion.life/register" className="flex items-center gap-2">
                {t('timelineDemo.cta.button')}
                <ArrowRight className="w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineDemo;
